/**
 * 유입 귀속(acquisition) 쿠키 — 광고 링크의 utm_*·fbclid 를 첫 랜딩에서 잡아 30일 들고 있다가
 * 체험 신청·가입·결제 요청에 실어 보낸다. 어드민 [이벤트관리 > 광고 링크] 가 소재별 퍼널을 이걸로 그린다.
 *
 * - 쿠키 도메인을 `shop3.io` 로 두어 shop3.io(랜딩)와 partner.shop3.io(가입·결제)가 같은 값을 읽는다.
 *   localStorage 는 오리진마다 따로라 도메인을 넘어가면 끊긴다.
 * - 첫 유입 우선(first-touch): 이미 쿠키가 있으면 덮지 않는다. 광고를 보고 온 사람이 나중에
 *   직접 주소로 들어와도 원래 소재에 귀속돼야 광고비 대비 성과가 성립한다.
 * - 비밀값이 아니다. 서버도 이 값을 통계에만 쓰고 권한·금액에는 쓰지 않는다.
 */

export const ACQ_COOKIE = 's3_acq';
/** 네이버 파워링크 클릭을 묶는 고정 코드·캠페인 (어드민 광고 링크 대장에 같은 코드로 등록) */
export const NAVER_POWERLINK_CODE = 'naver-pl';
export const NAVER_POWERLINK_CAMPAIGN = 'naver-powerlink';
/** 구글 광고 클릭을 묶는 고정 코드·캠페인. 구글 광고는 자동 태그(gclid)를 클릭 주소에 붙인다 */
export const GOOGLE_ADS_CODE = 'google-ads';
export const GOOGLE_ADS_CAMPAIGN = 'google-ads';
const MAX_AGE_SEC = 30 * 24 * 3600;

export interface Acq {
    source?: string;
    medium?: string;
    campaign?: string;
    term?: string;
    content?: string;
    fbclid?: string;
    landingUrl?: string;
}

/** 서버 AcqDto 와 같은 모양. fbp·fbc 는 Meta 픽셀이 심는 쿠키로, 전환 API 매칭에 쓴다 */
export interface AcqPayload extends Acq {
    fbp?: string;
    fbc?: string;
}

const KEYS: [keyof Acq, string][] = [
    ['source', 'utm_source'],
    ['medium', 'utm_medium'],
    ['campaign', 'utm_campaign'],
    ['term', 'utm_term'],
    ['content', 'utm_content'],
    ['fbclid', 'fbclid'],
];

/** 쿠키를 붙일 도메인. shop3.io 계열이면 상위 도메인, 아니면(localhost) 생략 */
function cookieDomain(): string | null {
    const host = window.location.hostname;
    return host === 'shop3.io' || host.endsWith('.shop3.io') ? 'shop3.io' : null;
}

function readCookie(name: string): string | null {
    try {
        const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
        return m ? m[1] : null;
    } catch {
        return null;
    }
}

export function readAcq(): Acq | null {
    const raw = readCookie(ACQ_COOKIE);
    if (!raw) return null;
    try {
        const obj = JSON.parse(decodeURIComponent(raw));
        if (!obj || typeof obj !== 'object') return null;
        const out: Acq = {};
        for (const [k] of KEYS) {
            const v = obj[k];
            if (typeof v === 'string' && v) out[k] = v.slice(0, 255);
        }
        if (typeof obj.landingUrl === 'string') out.landingUrl = obj.landingUrl.slice(0, 2000);
        return Object.keys(out).length ? out : null;
    } catch {
        return null;
    }
}

/**
 * 리퍼러로 유입 채널을 추정한다 — 광고 링크 없이 검색·SNS 에서 들어온 사람을 "구글 검색", "네이버 검색" 으로
 * 나눠 보기 위해서다. 우리 도메인 안에서의 이동(shop3.io ↔ partner.shop3.io)은 유입이 아니다.
 * 검색엔진은 organic, SNS 는 social. 그 외 외부 사이트는 referral.
 */
function acqFromReferrer(): Acq | null {
    const ref = document.referrer || '';
    if (!ref) return null;
    let host = '';
    try {
        host = new URL(ref).hostname.toLowerCase();
    } catch {
        return null;
    }
    if (!host || host === 'shop3.io' || host.endsWith('.shop3.io') || host === 'localhost') return null;
    const rules: [RegExp, string, string][] = [
        [/(^|\.)google\./, 'google', 'organic'],
        [/(^|\.)naver\.com$/, 'naver', 'organic'],
        [/(^|\.)daum\.net$/, 'daum', 'organic'],
        [/(^|\.)bing\.com$/, 'bing', 'organic'],
        [/(^|\.)instagram\.com$/, 'instagram', 'social'],
        [/(^|\.)(facebook\.com|fb\.com|messenger\.com)$/, 'facebook', 'social'],
        [/(^|\.)(youtube\.com|youtu\.be)$/, 'youtube', 'social'],
        [/(^|\.)tiktok\.com$/, 'tiktok', 'social'],
        [/(^|\.)kakao\.com$/, 'kakao', 'social'],
    ];
    for (const [re, source, medium] of rules) {
        if (re.test(host)) return { source, medium };
    }
    return { source: host.slice(0, 255), medium: 'referral' };
}

/**
 * 이번 방문이 어디서 왔는지 — 주소창(utm_*·fbclid → 네이버 파워링크 n_media…) 과 리퍼러만 본다. 쿠키는 안 본다.
 * 방문 로그가 이걸 쓴다: 재방문(북마크·직접 입력)을 예전 광고로 되돌려 세지 않기 위해서다.
 */
export function deriveAcqFromLocation(): Acq | null {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    const acq: Acq = {};
    let any = false;
    for (const [k, q] of KEYS) {
        const v = (params.get(q) || '').trim();
        if (v) {
            acq[k] = v.slice(0, 255);
            any = true;
        }
    }
    // 네이버 검색광고(파워링크) 클릭. 캠페인의 [추적 URL 설정]을 켜면 네이버가 n_media·n_keyword 등을 붙여 준다.
    // utm 이 없어도 이걸로 유료 클릭임을 안다. 코드 naver-pl 은 어드민 [전환율] 에 "네이버 파워링크" 로 등록돼 있다.
    if (!any && (params.has('n_media') || params.has('n_ad') || params.has('n_keyword_id'))) {
        acq.source = 'naver';
        acq.medium = 'cpc';
        acq.campaign = NAVER_POWERLINK_CAMPAIGN;
        acq.content = NAVER_POWERLINK_CODE;
        const kw = (params.get('n_keyword') || params.get('n_query') || '').trim();
        if (kw) acq.term = kw.slice(0, 255);
        any = true;
    }
    // 구글 광고 클릭. 광고 계정의 자동 태그가 켜져 있으면(기본값) gclid 가 붙는다. utm 이 있으면 그쪽을 우선한다.
    if (!any && (params.has('gclid') || params.has('gbraid') || params.has('wbraid'))) {
        acq.source = 'google';
        acq.medium = 'cpc';
        acq.campaign = GOOGLE_ADS_CAMPAIGN;
        acq.content = GOOGLE_ADS_CODE;
        any = true;
    }
    if (!any) {
        const byRef = acqFromReferrer();
        if (!byRef) return null;
        Object.assign(acq, byRef);
    }
    acq.landingUrl = window.location.href.slice(0, 2000);
    return acq;
}

/** 광고(유료·소재 코드)로 잡힌 귀속인지. 검색·SNS 자연 유입은 아니다 */
const isPaid = (a: Acq) => !!(a.content || a.campaign || a.fbclid);
const SESSION_ACQ_KEY = 's3_acq_session';

/**
 * 이번 탭(세션)에서 광고로 착지한 기록. 네이버 파워링크·구글 광고는 홈으로 착지하고 /try 는 SPA 이동이라
 * /try 의 주소창에는 광고 흔적이 없다. 방문 로그가 주소창·리퍼러로 못 찾을 때 이걸 쓴다.
 * 2026-09-10: 네이버 광고 클릭 → 체험 신청 2건인데 방문 0으로 찍힌 원인.
 */
export function readSessionAcq(): Acq | null {
    try {
        const raw = sessionStorage.getItem(SESSION_ACQ_KEY);
        if (!raw) return null;
        const obj = JSON.parse(raw);
        return obj && typeof obj === 'object' ? (obj as Acq) : null;
    } catch {
        return null;
    }
}

/**
 * 이번 방문의 유입을 쿠키에 담는다. 앱 렌더 전에 한 번 부른다.
 * 이미 광고로 잡힌 사람은 덮지 않는다(첫 광고 유지). 단, 검색으로 한 번 왔다가 나중에 광고를 눌러 들어오면
 * 광고 쪽으로 바꾼다 — 광고비 성과가 먼저다. 검색 유입끼리는 첫 것을 지킨다.
 */
export function captureAcqFromUrl(): void {
    try {
        const acq = deriveAcqFromLocation();
        if (!acq) return;
        // 이 탭에서 광고로 착지한 사실을 세션에도 남긴다. 방문 로그가 "홈으로 착지 → /try 로 이동" 을 광고 방문으로 세기 위해서.
        // 세션 저장소라 탭을 닫으면 사라지므로, 나중에 북마크로 다시 온 방문은 광고로 되세지 않는다.
        if (isPaid(acq)) {
            try {
                sessionStorage.setItem(SESSION_ACQ_KEY, JSON.stringify(acq));
            } catch {
                /* 저장 불가 환경은 넘어간다 */
            }
        }
        const existing = readAcq();
        if (existing && (isPaid(existing) || !isPaid(acq))) return;
        const domain = cookieDomain();
        document.cookie =
            `${ACQ_COOKIE}=${encodeURIComponent(JSON.stringify(acq))}; Max-Age=${MAX_AGE_SEC}; Path=/; SameSite=Lax` +
            (domain ? `; Domain=${domain}` : '') +
            (window.location.protocol === 'https:' ? '; Secure' : '');
    } catch {
        // 쿠키를 못 쓰는 환경은 귀속 없이 진행한다
    }
}

/** 요청 본문에 실을 값. 귀속 쿠키가 없어도 fbp·fbc 는 보낸다 (전환 API 매칭용) */
export function acqPayload(): AcqPayload | undefined {
    const acq = readAcq() || {};
    const fbp = readCookie('_fbp') || undefined;
    const fbc = readCookie('_fbc') || undefined;
    const out: AcqPayload = {
        ...acq,
        ...(fbp ? { fbp: fbp.slice(0, 64) } : {}),
        ...(fbc ? { fbc: fbc.slice(0, 255) } : {}),
    };
    return Object.keys(out).length ? out : undefined;
}

/** Meta 픽셀·전환 API 가 같은 건으로 합치는 열쇠. 브라우저가 만들고 서버에도 같은 값을 보낸다 */
export function newEventId(prefix: string): string {
    const rand =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    return `${prefix}:${rand}`.slice(0, 64);
}
